import { useState } from 'react';
import { Plus, Receipt, DollarSign, Calendar } from 'lucide-react';
import { useProjectExpenses } from '@/hooks/useProjects';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

interface ProjectExpensesProps {
  projectId: string;
  limit?: number;
}

export default function ProjectExpenses({ projectId, limit }: ProjectExpensesProps) {
  const { data: expenses, isLoading } = useProjectExpenses(projectId);
  
  const displayedExpenses = limit ? expenses?.slice(0, limit) : expenses;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'reimbursed':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'company_cost':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">
          {limit ? 'Recent Expenses' : 'Project Expenses'}
        </h3>
        {!limit && (
          <Button size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add Expense
          </Button>
        )}
      </div>

      {displayedExpenses && displayedExpenses.length > 0 ? (
        <div className="space-y-3">
          {displayedExpenses.map((expense) => (
            <div key={expense.id} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-start gap-3">
                <Receipt className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{expense.description}</h4>
                    <Badge className={getStatusColor(expense.reimbursement_status)}>
                      {expense.reimbursement_status.replace('_', ' ')}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    <span className="capitalize">{expense.expense_type}</span>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(expense.expense_date), 'MMM d, yyyy')}
                    </div>
                    {expense.vendor && <span>• {expense.vendor}</span>}
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="font-medium">{formatCurrency(expense.amount)}</div>
                {expense.submitted_by_user && (
                  <div className="text-xs text-muted-foreground">
                    by {expense.submitted_by_user.first_name} {expense.submitted_by_user.last_name}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {limit && expenses && expenses.length > limit && (
            <div className="text-center">
              <Button variant="link" className="text-sm">
                View all {expenses.length} expenses →
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Expenses</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Track project expenses for better cost management.
          </p>
          {!limit && (
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add First Expense
            </Button>
          )}
        </div>
      )}
    </div>
  );
}